import React, {useState, useRef, useEffect} from 'react'
import {Block, Col, Row} from 'jsxstyle'
import MessageList from './components/CopilotMessageList'
import DynamicFormField from '../../components/FormField'
import ChatHeader from './components/CopilotChatHeader'
import {useCopilotContext} from './CopilotContext'

const ChatView: React.FC = () => {
  const [newMessage, setNewMessage] = useState('')
  const [hasLoadedMessages, setHasLoadedMessages] = useState<{[key: string]: boolean}>({})
  const chatContainerRef = useRef<HTMLDivElement>(null)

  const {messages, streamingResponses, selectedConversation, isLoading, isConversationStreaming, fetchMessages, sendMessage} = useCopilotContext()

  const isCurrentConversationStreaming = selectedConversation ? isConversationStreaming(selectedConversation.id) : false

  const scrollToBottom = (immediate = false) => {
    if (chatContainerRef.current) {
      const timeout = immediate ? 0 : 50
      setTimeout(() => {
        const container = chatContainerRef.current
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      }, timeout)
    }
  }

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return
    const messageToSend = newMessage.trim()
    setNewMessage('')
    await sendMessage(selectedConversation.id, messageToSend)

    scrollToBottom()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  useEffect(() => {
    if (selectedConversation && !hasLoadedMessages[selectedConversation.id]) {
      setHasLoadedMessages((prev) => ({
        ...prev,
        [selectedConversation.id]: true,
      }))

      fetchMessages(selectedConversation.id)
    }
  }, [selectedConversation, fetchMessages, hasLoadedMessages])

  useEffect(() => {
    if (selectedConversation && messages[selectedConversation.id]?.length > 0) {
      scrollToBottom(true)
    }
  }, [selectedConversation, messages])

  useEffect(() => {
    if (isCurrentConversationStreaming) {
      scrollToBottom()
    }
  }, [streamingResponses, isCurrentConversationStreaming])

  useEffect(() => {
    scrollToBottom()
  }, [isCurrentConversationStreaming])

  const currentMessages = selectedConversation ? messages[selectedConversation.id] || [] : []
  const currentStreamingResponse = selectedConversation ? streamingResponses[selectedConversation.id] || '' : ''
  const hasMessage = newMessage.trim().length > 0

  return (
    <>
      <ChatHeader />
      <MessageList
        ref={chatContainerRef}
        messages={currentMessages}
        isLoading={isLoading}
        isStreaming={isCurrentConversationStreaming}
        streamingContent={currentStreamingResponse}
      />

      <Row
        padding="12px 16px"
        backgroundColor="#FFFFFF"
        width="100%"
        borderTop="1px solid #EEEEEE"
        alignItems="center"
        gap="10px"
        boxSizing="border-box"
      >
        <Block flex="1">
          <DynamicFormField
            placeholder="Type a message..."
            value={newMessage}
            onEdit={(value) => setNewMessage(value)}
            onSubmit={handleSendMessage}
            onKeyDown={handleKeyDown}
            disabled={isCurrentConversationStreaming}
          />
        </Block>
        <Block
          component="button"
          width="40px"
          height="40px"
          borderRadius="50%"
          backgroundColor={hasMessage && !isCurrentConversationStreaming ? '#0070f3' : '#DDDDDD'}
          display="flex"
          alignItems="center"
          justifyContent="center"
          cursor={hasMessage && !isCurrentConversationStreaming ? 'pointer' : 'not-allowed'}
          props={{
            onClick: hasMessage && !isCurrentConversationStreaming ? handleSendMessage : undefined,
            disabled: !hasMessage || isCurrentConversationStreaming,
          }}
          color="#fff"
          fontSize="18px"
          flexShrink={0}
        >
          →
        </Block>
      </Row>
    </>
  )
}

export default ChatView
